import { ChangeDetectionStrategy, Component, ElementRef, HostListener, effect, inject, viewChild } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SessionService } from '../session.service';
import { HEADER_NAV_ITEMS } from '../sportsbook/config/sportsbook-navigation.config';
import { HeaderNavItem } from '../sportsbook/sportsbook.models';
import { APP_BRAND } from '../branding/environment.config';
import { MobileNavigationService } from '../mobile-navigation.service';
import { ThemeSwitcherComponent } from './theme-switcher.component';
import { BalanceVisibilityService } from '../balance-visibility.service';

interface MobileNavDrawerSection {
  labelKey: string;
  items: readonly HeaderNavItem[];
}

@Component({
  selector: 'app-mobile-nav-drawer',
  standalone: true,
  imports: [A11yModule, CommonModule, RouterLink, RouterLinkActive, TranslateModule, ThemeSwitcherComponent],
  templateUrl: './mobile-nav-drawer.component.html',
  styleUrl: './mobile-nav-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileNavDrawerComponent {
  private readonly mobileNavigation = inject(MobileNavigationService);
  protected readonly open = this.mobileNavigation.isOpen;
  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');
  protected readonly session = inject(SessionService);
  protected readonly balanceVisibility = inject(BalanceVisibilityService);
  protected readonly brand = APP_BRAND;
  protected readonly navSections: readonly MobileNavDrawerSection[] = [
    {
      labelKey: 'navigation.sections.main',
      items: HEADER_NAV_ITEMS.filter(item => item.labelKey !== 'navigation.primary.settings')
    },
    {
      labelKey: 'navigation.sections.other',
      items: HEADER_NAV_ITEMS.filter(item => item.labelKey === 'navigation.primary.settings')
    }
  ];

  constructor() {
    effect(() => {
      if (!this.open()) {
        return;
      }

      queueMicrotask(() => {
        if (this.open()) {
          this.closeButton()?.nativeElement.focus({ preventScroll: true });
        }
      });
    });
  }

  close(): void {
    this.mobileNavigation.close();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    if (!this.open()) {
      return;
    }

    event.preventDefault();
    this.close();
  }
}
